// src/App.js
import React, { useState, useEffect, useRef } from "react";
import io from "socket.io-client";
import "./App.css";
import Chat from "./Chat";

function App() {
  const [username, setUsername] = useState("");
  const [room, setRoom] = useState("");
  const [showChat, setShowChat] = useState(false);
  const [userCount, setUserCount] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [isJoining, setIsJoining] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const socketRef = useRef(null);
  const activeSessionRef = useRef({ username: "", room: "", showChat: false });

  useEffect(() => {
    activeSessionRef.current = { username, room, showChat };
  }, [username, room, showChat]);

  useEffect(() => {
    const socketURL =
      process.env.REACT_APP_SOCKET_URL ||
      "https://spectrelink-backend.onrender.com";

    const socket = io(socketURL, {
      transports: ["websocket"],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("✅ Connected:", socket.id);
      const { username: activeUser, room: activeRoom, showChat: isChatActive } =
        activeSessionRef.current;

      if (isChatActive && activeUser && activeRoom) {
        console.log("🔄 Reconnected — re-claiming session identity...");
        setIsReconnecting(true);
        socket.emit("join_room", {
          room: activeRoom,
          username: activeUser,
          isReconnect: true,
        });
      }
    });

    socket.on("connect_error", (err) => {
      setErrorMessage("Connection error: " + err.message);
    });

    socket.on("disconnect", (reason) => {
      console.log("🔌 Disconnected from server:", reason);
      if (activeSessionRef.current.showChat) {
        setIsReconnecting(true);
      }
    });

    socket.on("room_data", (data) => {
      setUserCount(data.userCount);
    });

    socket.on("join_success", () => {
      setShowChat(true);
      setIsJoining(false);
      setIsReconnecting(false);
      setErrorMessage("");
    });

    socket.on("join_error", (data) => {
      setErrorMessage(data.message);
      setIsJoining(false);
      setIsReconnecting(false);
      setShowChat(false);
    });

    return () => {
      socket.off("connect");
      socket.off("connect_error");
      socket.off("disconnect");
      socket.off("room_data");
      socket.off("join_success");
      socket.off("join_error");
      socket.disconnect();
      socketRef.current = null;
    };
  }, []);

  const joinRoom = () => {
    if (username.trim() !== "" && room.trim() !== "") {
      if (!socketRef.current) return;
      setIsJoining(true);
      setErrorMessage("");
      socketRef.current.emit("join_room", { room, username });
    }
  };

  const handleLogout = () => {
    setShowChat(false);
    setUsername("");
    setRoom("");
    setUserCount(0);
    setErrorMessage("");
    setIsJoining(false);
    setIsReconnecting(false);
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
  };

  return (
    <div className="App">
      {isReconnecting && showChat && (
        <div className="reconnecting-banner">
          <span>⚠️ Connection lost. Reconnecting to room...</span>
        </div>
      )}
      {!showChat ? (
        <div className="joinChatContainer">
          <h3>Join A Chat</h3>
          {errorMessage && (
            <div
              className="error-message"
              style={{ color: "red", marginBottom: "10px" }}
            >
              {errorMessage}
            </div>
          )}
          <input
            type="text"
            placeholder="Your name"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && joinRoom()}
            disabled={isJoining}
          />
          <input
            type="text"
            placeholder="Room ID"
            value={room}
            onChange={(e) => setRoom(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && joinRoom()}
            disabled={isJoining}
          />
          <button onClick={joinRoom} disabled={isJoining}>
            {isJoining ? "Joining..." : "Join Room"}
          </button>
        </div>
      ) : (
        <Chat
          socket={socketRef.current}
          username={username}
          room={room}
          userCount={userCount}
          onLogout={handleLogout}
          isReconnecting={isReconnecting}
        />
      )}
    </div>
  );
}

export default App;
